/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * AnalyticsBarChart
 * -----------------
 * The Analytics headline chart — the web twin of UpVibe's AnalyticsBarChartView:
 * one rounded bar per bucket (day / week / month), no Y axis. The highlighted
 * bar (the selected one, else the latest with data) is the accent; the rest are
 * a light ink. A few X labels — first, last and evenly spaced between — sit
 * under the bars.
 *
 * Tap or drag across the bars to inspect a bucket (onSelect gets its index);
 * tapping the selected bar again clears it (-1). With a mouse, hovering a bar
 * previews it (onHover gets its index, -1 on leaving) without selecting it. Arrow keys / Home / End move
 * the selection, Escape clears it. The parent owns the selection, so it can
 * show that bucket's value in the headline and clear it when the data changes.
 */

import React, { useRef } from 'react';
import { tickIndices, highlightIndex } from '../../pages/feed/analytics/analyticsModel';

const LABEL_AREA = 22;
const DRAG_SLOP = 8;

export default function AnalyticsBarChart({
  points, selected = -1, hovered = -1, onSelect, onHover, height = 172, label = 'Trend chart',
}) {
  const plotRef = useRef(null);
  const gesture = useRef(null);
  const n = points.length;
  if (!n) return null;

  const max = Math.max(...points.map((p) => p.value), 0);
  const scaleMax = Math.max(max * 1.1, 0.1);
  // A hovered bar wins over the selected one, but only while the mouse is on it.
  const highlight = highlightIndex(points, hovered >= 0 ? hovered : selected);
  const ticks = tickIndices(n);
  const plotHeight = height - LABEL_AREA;

  const indexAt = (clientX) => {
    const rect = plotRef.current.getBoundingClientRect();
    const i = Math.floor(((clientX - rect.left) / Math.max(rect.width, 1)) * n);
    return Math.max(0, Math.min(n - 1, i));
  };
  const select = (i) => { if (i !== selected) onSelect(i); };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    gesture.current = { x: e.clientX, y: e.clientY, dragged: false, downIndex: selected, id: e.pointerId };
  };
  const hover = (i) => { if (onHover && i !== hovered) onHover(i); };
  const onPointerMove = (e) => {
    const g = gesture.current;
    if (e.pointerType === 'mouse' && !(g && g.dragged)) hover(indexAt(e.clientX));
    if (!g) return;
    const dx = Math.abs(e.clientX - g.x);
    const dy = Math.abs(e.clientY - g.y);
    if (!g.dragged && dx > DRAG_SLOP && dx > dy) {
      g.dragged = true;
      // Scrubbing sideways keeps the pointer even if it leaves the bars.
      try { e.currentTarget.setPointerCapture(g.id); } catch (err) { /* not capturable */ }
    }
    if (g.dragged) { hover(-1); select(indexAt(e.clientX)); }
  };
  const onPointerUp = (e) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.dragged) return;
    const i = indexAt(e.clientX);
    onSelect(i === g.downIndex ? -1 : i);
  };

  const onKeyDown = (e) => {
    hover(-1);
    const from = selected >= 0 ? selected : highlight;
    let next = null;
    if (e.key === 'ArrowRight') next = Math.min(n - 1, from + 1);
    else if (e.key === 'ArrowLeft') next = Math.max(0, from < 0 ? n - 1 : from - 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    else if (e.key === 'Escape' && selected >= 0) next = -1;
    if (next === null) return;
    e.preventDefault();
    select(next);
  };

  return (
    <div className="an-bars" style={{ height }}>
      <div
        ref={plotRef}
        className="an-bars-plot"
        style={{ height: plotHeight }}
        role="group"
        tabIndex={0}
        aria-label={`${label}, ${n} ${n === 1 ? 'bar' : 'bars'}. Use the arrow keys to inspect a bar.`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { gesture.current = null; hover(-1); }}
        onPointerLeave={() => hover(-1)}
        onKeyDown={onKeyDown}
      >
        {points.map((p, i) => {
          const h = Math.max((plotHeight * p.value) / scaleMax, p.value > 0 ? 3 : 1.5);
          return (
            <span key={p.key} className="an-bar-slot">
              <i className={`an-bar${i === highlight ? ' is-on' : ''}`} style={{ height: h }} />
            </span>
          );
        })}
      </div>
      <div className="an-bars-x" aria-hidden="true">
        {ticks.map((i) => {
          const align = n > 1 && i === 0 ? 'start' : n > 1 && i === n - 1 ? 'end' : 'mid';
          const style = align === 'start' ? { left: 0 }
            : align === 'end' ? { right: 0 }
              : { left: `${((i + 0.5) / n) * 100}%` };
          return <span key={i} className={`an-xl ${align}`} style={style}>{points[i].label}</span>;
        })}
      </div>
    </div>
  );
}
